<template>
  <div class="conversation-view-container">
    <div class="conversation-layout">
      <div class="conversation-main">
        <!-- Header -->
        <header class="conversation-header">
          <div class="header-content">
            <div class="header-info">
              <h1
                class="conversation-title"
                :class="{ 'title-placeholder': !conversation }"
              >
                {{
                  conversation
                    ? conversation.clean_subject
                    : "Loading conversation..."
                }}
              </h1>
              <div v-if="conversation" class="header-subtitle">
                <span class="message-count"
                  >{{ conversation.message_count }} messages</span
                >
                <span class="divider">•</span>
                <span class="participants">{{ getParticipantsText() }}</span>
                <span v-if="hasAttachments" class="divider">•</span>
                <span v-if="hasAttachments" class="attachment-indicator">
                  <svg
                    class="attachment-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                  >
                    <path
                      d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49"
                    />
                  </svg>
                  {{ totalAttachments }} attachments
                </span>
              </div>
            </div>

            <div class="header-actions">
              <button
                v-if="conversation"
                @click="replyToConversation"
                class="action-button reply-button"
              >
                <svg
                  class="button-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path d="M3 7v6h6" />
                  <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
                </svg>
                <span>Reply</span>
              </button>

              <button @click="goBack" class="action-button back-button">
                <svg
                  class="button-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path d="M19 12H5"></path>
                  <path d="M12 19l-7-7 7-7"></path>
                </svg>
                <span>Back</span>
              </button>
            </div>
          </div>
        </header>

        <!-- Main content -->
        <main class="conversation-content">
          <!-- Loading state -->
          <div v-if="loading" class="loading-state">
            <svg class="spinner" viewBox="0 0 50 50">
              <circle
                class="path"
                cx="25"
                cy="25"
                r="20"
                fill="none"
                stroke-width="4"
              ></circle>
            </svg>
            <p class="loading-text">Loading conversation...</p>
          </div>

          <!-- Error state -->
          <div v-else-if="error" class="error-state">
            <div class="error-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <p class="error-message">{{ error }}</p>
            <button @click="fetchConversation" class="retry-button">
              Try Again
            </button>
          </div>

          <!-- Conversation content -->
          <div
            v-else-if="conversation && conversation.messages"
            class="conversation-container"
          >
            <!-- Messages -->
            <div class="messages-list">
              <div
                v-for="(message, index) in sortedMessages"
                :key="message.id"
                class="message-item"
                :class="{
                  'is-reply': message.parent_message,
                  'is-last': index === sortedMessages.length - 1,
                }"
              >
                <!-- Message header -->
                <div class="message-header">
                  <div class="message-sender">
                    <div
                      class="sender-avatar"
                      :style="{
                        backgroundColor: getEmailColor(message.sender),
                      }"
                    >
                      {{ getEmailInitials(message.sender) }}
                    </div>
                    <div class="sender-info">
                      <div class="sender-name">{{ message.sender }}</div>
                      <div class="message-meta">
                        <span class="message-date">{{
                          formatDate(message.received_at || message.sent_at)
                        }}</span>
                        <span
                          v-if="message.message_type === 'sent'"
                          class="message-type"
                          >Sent</span
                        >
                        <span
                          v-else-if="message.message_type === 'draft'"
                          class="message-type"
                          >Draft</span
                        >
                      </div>
                    </div>
                  </div>

                  <div class="message-actions">
                    <button
                      v-if="!message.read"
                      @click="markAsRead(message)"
                      class="mark-read-btn"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <polyline points="20,6 9,17 4,12"></polyline>
                      </svg>
                    </button>
                    <button @click="replyToMessage(message)" class="reply-btn">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M3 7v6h6" />
                        <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Message content -->
                <div class="message-content">
                  <div
                    class="message-body"
                    v-html="
                      formatEmailContent(
                        message.html_content || message.content
                      )
                    "
                  ></div>

                  <!-- Attachments for this message -->
                  <div
                    v-if="
                      message.has_attachments &&
                      message.attachments &&
                      message.attachments.length > 0
                    "
                    class="message-attachments"
                  >
                    <div class="attachments-header">
                      <h4 class="attachments-title">
                        <svg
                          class="attachments-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49"
                          />
                        </svg>
                        {{ message.attachments.length }} attachment{{
                          message.attachments.length > 1 ? "s" : ""
                        }}
                      </h4>
                    </div>

                    <div class="attachments-list">
                      <div
                        v-for="attachment in message.attachments"
                        :key="attachment.id"
                        class="attachment-item"
                      >
                        <div class="attachment-icon-container">
                          <svg
                            v-if="attachment.is_document"
                            class="file-icon document"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            <path
                              d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"
                            />
                            <polyline points="14,2 14,8 20,8" />
                          </svg>
                          <svg
                            v-else-if="attachment.is_image"
                            class="file-icon image"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            <rect
                              width="18"
                              height="18"
                              x="3"
                              y="3"
                              rx="2"
                              ry="2"
                            />
                            <circle cx="9" cy="9" r="2" />
                            <path
                              d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"
                            />
                          </svg>
                          <svg
                            v-else-if="attachment.is_archive"
                            class="file-icon archive"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            <path
                              d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
                            />
                            <rect
                              width="8"
                              height="4"
                              x="8"
                              y="2"
                              rx="1"
                              ry="1"
                            />
                            <path d="M8 10h8" />
                            <path d="M8 14h8" />
                            <path d="M8 18h5" />
                          </svg>
                          <svg
                            v-else
                            class="file-icon default"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            <path
                              d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"
                            />
                            <polyline points="14,2 14,8 20,8" />
                          </svg>
                        </div>

                        <div class="attachment-details">
                          <div class="attachment-name">
                            {{ attachment.filename }}
                          </div>
                          <div class="attachment-meta">
                            <span class="attachment-size">{{
                              attachment.size_display
                            }}</span>
                            <span class="attachment-type">{{
                              getFileTypeLabel(attachment)
                            }}</span>
                          </div>
                        </div>

                        <div class="attachment-actions">
                          <button
                            @click="downloadAttachment(attachment)"
                            class="download-button"
                            :disabled="
                              downloadingAttachments.has(attachment.id)
                            "
                          >
                            <svg
                              v-if="!downloadingAttachments.has(attachment.id)"
                              class="download-icon"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                            >
                              <path
                                d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"
                              />
                              <polyline points="7,10 12,15 17,10" />
                              <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            <svg
                              v-else
                              class="spinner-small"
                              viewBox="0 0 50 50"
                            >
                              <circle
                                class="path"
                                cx="25"
                                cy="25"
                                r="20"
                                fill="none"
                                stroke-width="6"
                              ></circle>
                            </svg>
                            <span>{{
                              downloadingAttachments.has(attachment.id)
                                ? "Downloading..."
                                : "Download"
                            }}</span>
                          </button>

                          <button
                            v-if="
                              attachment.is_image ||
                              attachment.content_type === 'application/pdf'
                            "
                            @click="previewAttachment(attachment)"
                            class="preview-button"
                          >
                            <svg
                              class="preview-icon"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                            >
                              <path
                                d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                              />
                              <circle cx="12" cy="12" r="3" />
                            </svg>
                            <span>Preview</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import { downloadAttachment as apiDownloadAttachment } from "@/services/api";

export default {
  name: "ConversationView",
  setup() {
    const router = useRouter();
    const route = useRoute();
    const store = useStore();

    const conversationId = computed(() => route.params.conversationId);
    const conversation = ref(null);
    const loading = ref(true);
    const error = ref("");
    const downloadingAttachments = ref(new Set());

    // Computed properties
    const sortedMessages = computed(() => {
      if (!conversation.value?.messages) return [];

      const messages = [...conversation.value.messages];

      // Sort messages chronologically by received_at or sent_at
      messages.sort((a, b) => {
        const dateA = new Date(a.received_at || a.sent_at || a.created_at);
        const dateB = new Date(b.received_at || b.sent_at || b.created_at);
        return dateA - dateB;
      });

      return messages;
    });

    const hasAttachments = computed(() => {
      return sortedMessages.value.some((message) => message.has_attachments);
    });

    const totalAttachments = computed(() => {
      return sortedMessages.value.reduce((total, message) => {
        return total + (message.attachments?.length || 0);
      }, 0);
    });

    // Methods
    const fetchConversation = async () => {
      try {
        loading.value = true;
        error.value = "";
        const response = await store.dispatch(
          "fetchConversation",
          conversationId.value
        );
        conversation.value = response.data || response;
      } catch (err) {
        error.value = err.message || "Failed to load conversation";
      } finally {
        loading.value = false;
      }
    };

    const markAsRead = async (message) => {
      try {
        await store.dispatch("markMessageAsRead", message.id);
        message.read = true;
      } catch (err) {
        console.error("Failed to mark message as read:", err);
      }
    };

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const formatEmailContent = (content) => {
      if (!content) return "";

      // Normalize content and remove unnecessary whitespace
      const normalizedContent = content.trim();

      // Check if content is HTML
      if (isHTMLContent(normalizedContent)) {
        return processHTMLContent(normalizedContent);
      }

      // Process as plain text
      return processPlainTextContent(normalizedContent);
    };

    const isHTMLContent = (content) => {
      // More sophisticated HTML detection
      const htmlPatterns = [
        /<(!DOCTYPE\s+html|html|head|body|meta|title|style|script|link|div|p|span|table|tr|td|th|ul|ol|li|h[1-6]|br|img|a)\b[^>]*>/i,
        /&(amp|lt|gt|quot|#\d+);/,
        /<\/[a-z]+>/i,
      ];

      return htmlPatterns.some((pattern) => pattern.test(content));
    };

    const processHTMLContent = (htmlContent) => {
      try {
        // Clean and sanitize HTML content
        let processedHTML = sanitizeHTML(htmlContent);

        // Apply Gmail/Outlook-style enhancements
        processedHTML = enhanceHTMLForDisplay(processedHTML);

        // Wrap in container with proper styling
        return `<div class="email-html-content modern-email-display">${processedHTML}</div>`;
      } catch (error) {
        console.warn(
          "HTML processing failed, falling back to plain text:",
          error
        );
        // Strip HTML tags and process as plain text if HTML processing fails
        const plainText = htmlContent
          .replace(/<[^>]*>/g, "")
          .replace(/&[^;]+;/g, " ");
        return processPlainTextContent(plainText);
      }
    };

    const sanitizeHTML = (html) => {
      // Pre-process HTML to fix common malformations
      let cleanedHTML = html
        // Fix malformed URLs with extra slashes
        .replace(/https:\/\/\/\/+/gi, "https://")
        // Fix broken URLs that span multiple lines or are malformed
        .replace(/<https:\s*([^>]+)>/gi, (match, urlParts) => {
          // Reconstruct the URL from fragmented parts
          const cleanUrl = urlParts
            .replace(/\s+/g, "")
            .replace(/=""/g, "/")
            .replace(/="/g, "/");
          return `https://${cleanUrl}`;
        })
        // Fix URLs that appear as plain text with malformed protocols
        .replace(/https:\/\/\/\/([^\s<>]+)/gi, "https://$1")
        // Fix malformed link structures
        .replace(/([A-Z\s]+)\s*<(https?:\/\/[^>]+)>/gi, (match, text, url) => {
          const cleanText = text.trim();
          const cleanUrl = url.trim();
          // Create a proper link for button-like text
          if (
            /^(APPLY|CLICK|DOWNLOAD|START|GET|JOIN|SIGN UP|REGISTER|LEARN MORE|READ MORE|VIEW|SEE|TRY|BUY|SHOP|ORDER|CONTACT|CALL|EMAIL)[\s\w]*$/i.test(
              cleanText
            )
          ) {
            return `<a href="${cleanUrl}" class="email-button-link" target="_blank" rel="noopener noreferrer">${cleanText}</a>`;
          }
          return `<a href="${cleanUrl}" class="email-link" target="_blank" rel="noopener noreferrer">${cleanText}</a>`;
        })
        // Handle plain text followed by URLs
        .replace(/([^<>\n]+)\s*<(https?:\/\/[^>]+)>/gi, (match, text, url) => {
          const cleanText = text.trim();
          const cleanUrl = url.trim();
          if (cleanText && !cleanText.includes("<") && cleanText.length > 0) {
            return `<a href="${cleanUrl}" class="email-link" target="_blank" rel="noopener noreferrer">${cleanText}</a>`;
          }
          return match;
        });

      // Create a temporary container for parsing
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = cleanedHTML;

      // Remove dangerous elements and attributes
      const dangerousTags = [
        "script",
        "object",
        "embed",
        "form",
        "input",
        "button",
      ];
      const dangerousAttrs = [
        "onload",
        "onclick",
        "onmouseover",
        "onmouseout",
        "onfocus",
        "onblur",
        "onchange",
        "onsubmit",
      ];

      // Remove dangerous tags
      dangerousTags.forEach((tag) => {
        const elements = tempDiv.querySelectorAll(tag);
        elements.forEach((el) => el.remove());
      });

      // Remove dangerous attributes
      const allElements = tempDiv.querySelectorAll("*");
      allElements.forEach((el) => {
        dangerousAttrs.forEach((attr) => {
          if (el.hasAttribute(attr)) {
            el.removeAttribute(attr);
          }
        });

        // Sanitize href attributes
        if (el.hasAttribute("href")) {
          const href = el.getAttribute("href");
          if (href && !href.match(/^(https?:|mailto:|tel:)/i)) {
            el.removeAttribute("href");
          }
        }
      });

      return tempDiv.innerHTML;
    };

    const enhanceHTMLForDisplay = (html) => {
      return (
        html
          // Fix responsive images
          .replace(/<img([^>]*?)>/gi, (match, attrs) => {
            // Add responsive attributes if not present
            if (!attrs.includes("style=") && !attrs.includes("class=")) {
              return `<img${attrs} class="email-image">`;
            }
            return match;
          })

          // Enhance tables for better mobile display
          .replace(/<table([^>]*?)>/gi, (match, attrs) => {
            if (!attrs.includes("class=")) {
              return `<table${attrs} class="email-table">`;
            }
            return match;
          })

          // Enhance links
          .replace(/<a([^>]*?)>/gi, (match, attrs) => {
            let enhancedAttrs = attrs;

            // Add security attributes if not present
            if (!attrs.includes("target=")) {
              enhancedAttrs += ' target="_blank"';
            }
            if (!attrs.includes("rel=")) {
              enhancedAttrs += ' rel="noopener noreferrer"';
            }

            // Add styling class if not present
            if (!attrs.includes("class=")) {
              enhancedAttrs += ' class="email-link"';
            }

            return `<a${enhancedAttrs}>`;
          })

          // Enhance paragraphs
          .replace(/<p([^>]*?)>/gi, (match, attrs) => {
            if (!attrs.includes("class=")) {
              return `<p${attrs} class="email-paragraph">`;
            }
            return match;
          })

          // Convert old-style font tags to spans with inline styles
          .replace(/<font([^>]*?)>(.*?)<\/font>/gi, (match, attrs, content) => {
            let style = "";
            const faceMatch = attrs.match(/face=["']([^"']+)["']/i);
            const sizeMatch = attrs.match(/size=["']([^"']+)["']/i);
            const colorMatch = attrs.match(/color=["']([^"']+)["']/i);

            if (faceMatch) style += `font-family: ${faceMatch[1]};`;
            if (sizeMatch) {
              const size = parseInt(sizeMatch[1]);
              const fontSize =
                size <= 1
                  ? "0.8em"
                  : size === 2
                  ? "0.9em"
                  : size === 3
                  ? "1em"
                  : size === 4
                  ? "1.1em"
                  : size === 5
                  ? "1.2em"
                  : "1.4em";
              style += `font-size: ${fontSize};`;
            }
            if (colorMatch) style += `color: ${colorMatch[1]};`;

            return style
              ? `<span style="${style}" class="email-styled-text">${content}</span>`
              : content;
          })

          // Convert center tags to div with text-align
          .replace(
            /<center>(.*?)<\/center>/gi,
            '<div class="email-center">$1</div>'
          )

          // Handle Outlook-specific MSO conditional comments
          .replace(/<!--\[if mso\]>.*?<!\[endif\]-->/gi, "")

          // Clean up excessive whitespace while preserving intentional spacing
          .replace(/\n\s*\n\s*\n/g, "\n\n")

          // Add structure classes for better styling
          .replace(/<div([^>]*?)>/gi, (match, attrs) => {
            if (!attrs.includes("class=")) {
              return `<div${attrs} class="email-div">`;
            }
            return match;
          })
      );
    };

    const processPlainTextContent = (content) => {
      // Enhanced plain text processing similar to Gmail/Outlook

      // Escape HTML characters
      const escapeHtml = (text) => {
        const htmlEntities = {
          "&": "&amp;",
          "<": "&lt;",
          ">": "&gt;",
          '"': "&quot;",
          "'": "&#x27;",
          "/": "&#x2F;",
        };
        return text.replace(/[&<>"'/]/g, (match) => htmlEntities[match]);
      };

      let processedContent = escapeHtml(content);

      // First, detect and convert action text followed by URLs (like "APPLY NOW https://...")
      const actionUrlRegex =
        /([A-Z][A-Z\s&]+?)\s+((?:https?:\/\/|www\.)[^\s<>(){}[\]"']+[^\s<>(){}[\]"'.,;:!])/gi;
      processedContent = processedContent.replace(
        actionUrlRegex,
        (match, actionText, url) => {
          const cleanActionText = actionText.trim();
          let href = url;

          // Add protocol if missing
          if (url.toLowerCase().startsWith("www.")) {
            href = "https://" + url;
          }

          // Check if this looks like a button action
          if (
            /^(APPLY|CLICK|DOWNLOAD|START|GET|JOIN|SIGN UP|REGISTER|LEARN MORE|READ MORE|VIEW|SEE|TRY|BUY|SHOP|ORDER|CONTACT|CALL|EMAIL|SCHEDULE|SUBMIT|UPDATE|MANAGE|CHECK OUT)[\s\w&]*$/i.test(
              cleanActionText
            )
          ) {
            return `<a href="${href}" class="email-button-link" target="_blank" rel="noopener noreferrer">${cleanActionText}</a>`;
          } else {
            return `<a href="${href}" class="email-link" target="_blank" rel="noopener noreferrer">${cleanActionText}</a>`;
          }
        }
      );

      // Also handle descriptive text followed by URLs (like "Check out our website https://...")
      const descriptiveUrlRegex =
        /([A-Za-z][^.!?]*?(?:website|portal|page|link|survey|form|application|here|below|above))\s+((?:https?:\/\/|www\.)[^\s<>(){}[\]"']+[^\s<>(){}[\]"'.,;:!])/gi;
      processedContent = processedContent.replace(
        descriptiveUrlRegex,
        (match, descriptiveText, url) => {
          const cleanDescriptiveText = descriptiveText.trim();
          let href = url;

          // Add protocol if missing
          if (url.toLowerCase().startsWith("www.")) {
            href = "https://" + url;
          }

          return `<a href="${href}" class="email-link" target="_blank" rel="noopener noreferrer">${cleanDescriptiveText}</a>`;
        }
      );

      // Enhanced URL detection and linking (process remaining URLs)
      const advancedUrlRegex =
        /((?:https?:\/\/|www\.)[^\s<>(){}[\]"']+[^\s<>(){}[\]"'.,;:])/gi;

      // Create placeholders for URLs to protect them during phone number processing
      const urlPlaceholders = new Map();
      let urlCounter = 0;

      processedContent = processedContent.replace(advancedUrlRegex, (url) => {
        let href = url;
        let displayUrl = url;

        // Add protocol if missing
        if (url.toLowerCase().startsWith("www.")) {
          href = "https://" + url;
        }

        // Truncate very long URLs for display
        if (displayUrl.length > 50) {
          const domain =
            displayUrl.match(/^(?:https?:\/\/)?([^/]+)/)?.[1] || "";
          displayUrl =
            domain.length > 30
              ? `${domain.substring(0, 30)}...`
              : `${domain}...`;
        }

        const formattedLink = `<a href="${href}" class="email-link" target="_blank" rel="noopener noreferrer" title="${escapeHtml(
          url
        )}">${displayUrl}</a>`;

        // Store the formatted link and return a placeholder
        const placeholder = `__URL_PLACEHOLDER_${urlCounter++}__`;
        urlPlaceholders.set(placeholder, formattedLink);
        return placeholder;
      });

      // Enhanced email detection
      const emailRegex =
        /\b[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*\b/g;
      processedContent = processedContent.replace(emailRegex, (email) => {
        return `<a href="mailto:${email}" class="email-link email-address" title="Send email to ${escapeHtml(
          email
        )}">
          <span class="email-icon">✉</span>${email}
        </a>`;
      });

      // Enhanced phone number detection (more precise patterns)
      const phoneRegex =
        /(?:(?:^|[^\w/])(?:\+?1[-.\s]?)?\(?[2-9][0-9]{2}\)?[-.\s]?[2-9][0-9]{2}[-.\s]?[0-9]{4}(?![/\w])|(?:^|[^\w/])\+[1-9]\d{1,3}[-.\s]?\d{1,14}(?![/\w]))/g;
      processedContent = processedContent.replace(phoneRegex, (match) => {
        // Extract just the phone number part, removing any leading/trailing non-phone characters
        const phoneMatch = match.match(
          /(\+?1[-.\s]?)?\(?[2-9][0-9]{2}\)?[-.\s]?[2-9][0-9]{2}[-.\s]?[0-9]{4}|\+[1-9]\d{1,3}[-.\s]?\d{1,14}/
        );
        if (!phoneMatch) return match;

        const phone = phoneMatch[0];
        const beforePhone = match.substring(0, match.indexOf(phone));
        const afterPhone = match.substring(match.indexOf(phone) + phone.length);

        // Additional validation: exclude obvious non-phone patterns
        if (
          phone.length > 15 || // Too long for a phone number
          (/^\d{10,}$/.test(phone.replace(/[^\d]/g, "")) &&
            phone.replace(/[^\d]/g, "").length > 12) || // Long number without formatting
          /^1{3,}/.test(phone.replace(/[^\d]/g, "")) // Repeated 1s (likely tracking IDs)
        ) {
          return match;
        }

        const cleanPhone = phone.replace(/[^\d+]/g, "");
        return `${beforePhone}<a href="tel:${cleanPhone}" class="email-link phone-number" title="Call ${escapeHtml(
          phone
        )}">
          <span class="phone-icon">📞</span>${phone}
        </a>${afterPhone}`;
      });

      // Detect and format code blocks (common in technical emails)
      processedContent = processedContent.replace(
        /```([\s\S]*?)```/g,
        '<pre class="email-code-block"><code>$1</code></pre>'
      );

      // Detect inline code
      processedContent = processedContent.replace(
        /`([^`\n]+)`/g,
        '<code class="email-inline-code">$1</code>'
      );

      // Smart paragraph detection and formatting
      const paragraphs = processedContent.split(/\n\s*\n+/);
      let formattedParagraphs = paragraphs
        .map((paragraph) => {
          if (!paragraph.trim()) return "";

          let p = paragraph
            .replace(/\n/g, "<br>")
            .replace(/\s{2,}/g, (spaces) => "&nbsp;".repeat(spaces.length));

          // Detect headers (lines that are all caps or follow specific patterns)
          if (
            /^[A-Z\s]{10,}$/.test(paragraph.trim()) ||
            /^(RE:|FW:|SUBJECT:|FROM:|TO:|DATE:)/i.test(paragraph.trim())
          ) {
            return `<div class="email-header">${p}</div>`;
          }

          // Detect list items
          if (/^\s*[-*•]\s/.test(paragraph)) {
            const listItems = paragraph.split("\n").map((line) => {
              if (/^\s*[-*•]\s/.test(line)) {
                return `<li class="email-list-item">${line.replace(
                  /^\s*[-*•]\s/,
                  ""
                )}</li>`;
              }
              return line;
            });
            return `<ul class="email-list">${listItems.join("")}</ul>`;
          }

          // Detect numbered lists
          if (/^\s*\d+\.\s/.test(paragraph)) {
            const listItems = paragraph.split("\n").map((line) => {
              if (/^\s*\d+\.\s/.test(line)) {
                return `<li class="email-list-item">${line.replace(
                  /^\s*\d+\.\s/,
                  ""
                )}</li>`;
              }
              return line;
            });
            return `<ol class="email-list">${listItems.join("")}</ol>`;
          }

          return `<p class="email-paragraph">${p}</p>`;
        })
        .filter((p) => p);

      // Detect and format email signatures
      const signaturePatterns = [
        /^(--|__)/,
        /(?:Best regards?|Sincerely|Thank you|Thanks|Regards|Kind regards|Cheers|Best wishes)\s*,?\s*$/i,
        /^Sent from my \w+/i,
      ];

      let inSignature = false;
      formattedParagraphs = formattedParagraphs.map((paragraph) => {
        if (!inSignature) {
          for (const pattern of signaturePatterns) {
            if (pattern.test(paragraph.replace(/<[^>]+>/g, ""))) {
              inSignature = true;
              break;
            }
          }
        }

        if (inSignature) {
          return paragraph.replace(/^<p/, '<p class="email-signature"');
        }

        return paragraph;
      });

      // Detect quoted content (common in email replies)
      const quotedContent = formattedParagraphs.map((paragraph) => {
        const textContent = paragraph.replace(/<[^>]+>/g, "");
        if (
          textContent.startsWith(">") ||
          /^On .+ wrote:$/i.test(textContent)
        ) {
          return paragraph.replace(/^<([^>]+)/, "<$1 email-quoted");
        }
        return paragraph;
      });

      let finalContent = `<div class="email-content-formatted modern-email-display">${quotedContent.join(
        ""
      )}</div>`;

      // Restore URL placeholders with their formatted links
      urlPlaceholders.forEach((formattedLink, placeholder) => {
        finalContent = finalContent.replace(placeholder, formattedLink);
      });

      return finalContent;
    };

    const getEmailColor = (emailAddress) => {
      if (!emailAddress) return "#3b82f6";
      const colors = [
        "#3b82f6",
        "#8b5cf6",
        "#ec4899",
        "#f97316",
        "#14b8a6",
        "#f59e0b",
        "#10b981",
        "#6366f1",
        "#ef4444",
        "#84cc16",
      ];
      let hash = 0;
      for (let i = 0; i < emailAddress.length; i++) {
        hash = emailAddress.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    };

    const getEmailInitials = (emailAddress) => {
      if (!emailAddress) return "??";
      const localPart = emailAddress.split("@")[0];
      return localPart.slice(0, 2).toUpperCase();
    };

    const getParticipantsText = () => {
      if (!conversation.value?.participants_list) return "";
      const participants = conversation.value.participants_list;
      if (participants.length <= 2) {
        return participants.join(", ");
      }
      return `${participants.slice(0, 2).join(", ")} and ${
        participants.length - 2
      } others`;
    };

    const getFileTypeLabel = (attachment) => {
      if (attachment.is_document) {
        if (attachment.content_type === "application/pdf")
          return "PDF Document";
        if (attachment.content_type.includes("word")) return "Word Document";
        if (attachment.content_type.includes("excel"))
          return "Excel Spreadsheet";
        if (attachment.content_type.includes("powerpoint"))
          return "PowerPoint Presentation";
        return "Document";
      }
      if (attachment.is_image) return "Image";
      if (attachment.is_archive) return "Archive";
      return "File";
    };

    const downloadAttachment = async (attachment) => {
      try {
        downloadingAttachments.value.add(attachment.id);
        const response = await apiDownloadAttachment(attachment.id);

        const blob = new Blob([response.data], {
          type:
            response.headers["content-type"] ||
            attachment.content_type ||
            "application/octet-stream",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = attachment.filename;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error("Failed to download attachment:", err);
        let errorMessage = "Failed to download attachment";
        if (err.response?.status === 404) {
          errorMessage = "Attachment not found";
        } else if (err.response?.status === 403) {
          errorMessage = "Access denied to attachment";
        }
        alert(errorMessage);
      } finally {
        setTimeout(() => {
          downloadingAttachments.value.delete(attachment.id);
        }, 1000);
      }
    };

    const previewAttachment = async (attachment) => {
      try {
        const response = await apiDownloadAttachment(attachment.id);
        const blob = new Blob([response.data], {
          type:
            response.headers["content-type"] ||
            attachment.content_type ||
            "application/octet-stream",
        });
        const url = window.URL.createObjectURL(blob);

        window.open(url, "_blank");

        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      } catch (err) {
        console.error("Failed to preview attachment:", err);
        alert("Failed to preview attachment");
      }
    };

    const replyToMessage = (message) => {
      // Find the last received message in the conversation
      let lastReceivedMessage = null;
      if (sortedMessages.value && sortedMessages.value.length > 0) {
        // Sort by date (most recent first) and find the last received message
        const messagesSortedByDate = [...sortedMessages.value].sort((a, b) => {
          const dateA = new Date(a.received_at || a.sent_at || a.created_at);
          const dateB = new Date(b.received_at || b.sent_at || b.created_at);
          return dateB - dateA;
        });

        lastReceivedMessage = messagesSortedByDate.find(
          (msg) => msg.message_type === "received"
        );
      }

      document.dispatchEvent(
        new CustomEvent("open-email-composer", {
          detail: {
            mode: "reply",
            message: message,
            conversation: conversation.value,
            lastReceivedMessage: lastReceivedMessage, // Pass the last received message
            fromEmailId: message.email_address?.id,
            fromEmail: message.email_address?.email,
          },
        })
      );
    };

    const replyToConversation = () => {
      if (sortedMessages.value.length > 0) {
        const lastMessage =
          sortedMessages.value[sortedMessages.value.length - 1];
        replyToMessage(lastMessage);
      }
    };

    const goBack = () => {
      // Check where we came from using query parameters
      const fromPage = route.query.from;
      const tab = route.query.tab;
      const emailId = route.query.emailId;

      if (fromPage === "dashboard" || fromPage === "messages") {
        // Navigate back to Dashboard with the appropriate tab active
        router.push({
          name: "Dashboard",
          query: { tab: tab || "messages" },
        });
      } else if (fromPage === "inbox" && emailId) {
        // Navigate back to the specific inbox
        router.push({
          name: "Inbox",
          params: { id: emailId },
        });
      } else if (fromPage === "inbox") {
        // Fallback to dashboard if no emailId provided
        router.push({ name: "Dashboard" });
      } else {
        // Default behavior - go back to the previous page
        router.go(-1);
      }
    };

    // Lifecycle
    onMounted(async () => {
      await fetchConversation();

      // Mark conversation as read
      if (conversation.value) {
        try {
          await store.dispatch("markConversationAsRead", conversationId.value);
        } catch (err) {
          console.error("Failed to mark conversation as read:", err);
        }
      }
    });

    return {
      conversation,
      loading,
      error,
      downloadingAttachments,
      sortedMessages,
      hasAttachments,
      totalAttachments,
      fetchConversation,
      markAsRead,
      formatDate,
      formatEmailContent,
      getEmailColor,
      getEmailInitials,
      getParticipantsText,
      getFileTypeLabel,
      downloadAttachment,
      previewAttachment,
      replyToMessage,
      replyToConversation,
      goBack,
    };
  },
};
</script>

<style lang="scss" scoped>
// Variables
$primary-color: #3b82f6;
$primary-hover: #2563eb;
$primary-light: #eff6ff;
$success-color: #10b981;
$error-color: #ef4444;
$error-light: #fee2e2;
$error-dark: #b91c1c;

$text-primary: #111827;
$text-secondary: #4b5563;
$text-muted: #6b7280;
$text-light: #9ca3af;
$text-white: #ffffff;

$border-color: #e5e7eb;
$border-dark: #d1d5db;

$bg-white: #ffffff;
$bg-light: #f9fafb;
$bg-gray: #f3f4f6;
$bg-hover: #e5e7eb;

$shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
$shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
$shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
  0 2px 4px -1px rgba(0, 0, 0, 0.06);
$shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
  0 4px 6px -2px rgba(0, 0, 0, 0.05);

$radius-sm: 0.25rem;
$radius: 0.375rem;
$radius-md: 0.5rem;
$radius-lg: 0.75rem;
$radius-full: 9999px;

$transition: all 0.2s ease;

// Base container
.conversation-view-container {
  width: 100%;
  height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  color: $text-primary;
  background-color: $bg-gray;
  overflow: hidden;
}

// Main layout
.conversation-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.conversation-main {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: hidden;
}

// Header styling
.conversation-header {
  background-color: $bg-white;
  border-bottom: 1px solid $border-color;
  box-shadow: $shadow;
  z-index: 10;

  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1.5rem;
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
      padding: 1rem;
    }
  }

  .header-info {
    flex: 1;
    min-width: 0;

    .conversation-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: $text-primary;
      margin: 0;
      line-height: 1.4;

      &.title-placeholder {
        color: $text-muted;
        background-color: $bg-light;
        border-radius: $radius;
        display: inline-block;
        padding: 0.25rem 1rem;
        animation: pulse 1.5s infinite;
      }
    }

    .header-subtitle {
      display: flex;
      align-items: center;
      font-size: 0.875rem;
      color: $text-secondary;
      margin-top: 0.375rem;
      gap: 0.5rem;
      flex-wrap: wrap;

      .message-count {
        font-weight: 500;
      }

      .divider {
        color: $text-light;
      }

      .participants {
        color: $text-muted;
      }

      .attachment-indicator {
        display: flex;
        align-items: center;
        gap: 0.25rem;
        color: $primary-color;
        font-weight: 500;

        .attachment-icon {
          width: 1rem;
          height: 1rem;
        }
      }
    }
  }

  .header-actions {
    display: flex;
    gap: 0.75rem;
    flex-shrink: 0;

    @media (max-width: 768px) {
      gap: 0.5rem;
    }
  }
}

// Action buttons
.action-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.875rem;
  border: 1px solid $border-color;
  border-radius: $radius;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: $transition;
  text-decoration: none;

  .button-icon {
    width: 1.125rem;
    height: 1.125rem;
    stroke-width: 2;
    flex-shrink: 0;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba($primary-color, 0.15);
  }

  &.reply-button {
    background-color: $success-color;
    border-color: $success-color;
    color: $text-white;

    &:hover {
      background-color: darken($success-color, 10%);
      border-color: darken($success-color, 10%);
    }
  }

  &.back-button {
    background-color: $bg-light;
    border-color: $border-color;
    color: $text-secondary;

    &:hover {
      background-color: $bg-hover;
      color: $text-primary;
    }
  }

  @media (max-width: 640px) {
    span {
      display: none;
    }
    padding: 0.5rem;
  }
}

// Content area
.conversation-content {
  flex: 1;
  overflow: auto;
  padding: 1.5rem;
  background-color: $bg-gray;
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }
}

// Loading and error states
.loading-state,
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  background-color: $bg-white;
  border-radius: $radius-md;
  box-shadow: $shadow-md;
}

.loading-state {
  .spinner {
    width: 3.5rem;
    height: 3.5rem;
    animation: rotate 2s linear infinite;
    margin-bottom: 1.5rem;

    .path {
      stroke: $primary-color;
      stroke-linecap: round;
      animation: dash 1.5s ease-in-out infinite;
    }
  }

  .loading-text {
    color: $text-secondary;
    font-size: 1rem;
    margin: 0;
  }
}

.error-state {
  .error-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 4rem;
    height: 4rem;
    margin-bottom: 1.5rem;
    background-color: $error-light;
    border-radius: $radius-full;
    color: $error-color;

    svg {
      width: 2rem;
      height: 2rem;
    }
  }

  .error-message {
    color: $error-dark;
    font-size: 1rem;
    margin: 0 0 1.5rem;
  }

  .retry-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1.25rem;
    background-color: $primary-color;
    border: none;
    border-radius: $radius;
    color: $text-white;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: $transition;

    &:hover {
      background-color: $primary-hover;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba($primary-color, 0.3);
    }
  }
}

// Conversation container
.conversation-container {
  background-color: $bg-white;
  border-radius: $radius-md;
  box-shadow: $shadow-md;
  overflow: hidden;
}

// Messages list
.messages-list {
  display: flex;
  flex-direction: column;
}

.message-item {
  border-bottom: 1px solid $border-color;

  &.is-reply {
    margin-left: 2rem;
    border-left: 3px solid $primary-color;

    @media (max-width: 640px) {
      margin-left: 1rem;
    }
  }

  &.is-last {
    border-bottom: none;
  }

  &:hover {
    background-color: rgba($primary-color, 0.02);
  }
}

// Message header
.message-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 0.75rem;

  .message-sender {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;

    .sender-avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2.5rem;
      height: 2.5rem;
      border-radius: $radius-full;
      color: white;
      font-size: 0.75rem;
      font-weight: 700;
      flex-shrink: 0;
    }

    .sender-info {
      flex: 1;
      min-width: 0;

      .sender-name {
        font-size: 0.875rem;
        font-weight: 600;
        color: $text-primary;
        margin-bottom: 0.25rem;
      }

      .message-meta {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.75rem;
        color: $text-muted;

        .message-type {
          padding: 0.125rem 0.5rem;
          background-color: $primary-light;
          color: $primary-color;
          border-radius: $radius-sm;
          font-weight: 500;
        }
      }
    }
  }

  .message-actions {
    display: flex;
    gap: 0.5rem;
    opacity: 0;
    transition: $transition;

    button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      background: transparent;
      border: 1px solid $border-color;
      border-radius: $radius-sm;
      color: $text-muted;
      cursor: pointer;
      transition: $transition;

      &:hover {
        color: $primary-color;
        border-color: $primary-color;
        background-color: $primary-light;
      }

      svg {
        width: 0.875rem;
        height: 0.875rem;
      }
    }
  }

  &:hover .message-actions {
    opacity: 1;
  }
}

// Message content
.message-content {
  padding: 0 1.5rem 1.25rem;

  .message-body {
    line-height: 1.6;
    color: $text-primary;
    font-size: 0.875rem;
    margin-bottom: 1rem;
    white-space: pre-wrap;
    word-wrap: break-word;
  }
}

// Message attachments
.message-attachments {
  border-top: 1px solid $border-color;
  padding-top: 1rem;
  margin-top: 1rem;

  .attachments-header {
    margin-bottom: 0.75rem;

    .attachments-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: $text-primary;
      margin: 0;

      .attachments-icon {
        width: 1rem;
        height: 1rem;
        color: $primary-color;
      }
    }
  }

  .attachments-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .attachment-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background-color: $bg-light;
    border: 1px solid $border-color;
    border-radius: $radius;
    transition: $transition;

    &:hover {
      border-color: $border-dark;
      box-shadow: $shadow-sm;
    }

    .attachment-icon-container {
      flex-shrink: 0;
      width: 2.5rem;
      height: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: $radius;
      background-color: $bg-white;

      .file-icon {
        width: 1.25rem;
        height: 1.25rem;

        &.document {
          color: #dc2626;
        }
        &.image {
          color: #059669;
        }
        &.archive {
          color: #7c2d12;
        }
        &.default {
          color: $text-muted;
        }
      }
    }

    .attachment-details {
      flex: 1;
      min-width: 0;

      .attachment-name {
        font-weight: 500;
        color: $text-primary;
        font-size: 0.75rem;
        margin-bottom: 0.25rem;
        word-break: break-word;
        line-height: 1.4;
      }

      .attachment-meta {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.625rem;
        color: $text-muted;

        .attachment-size {
          font-weight: 500;
        }

        .attachment-type {
          &::before {
            content: "•";
            margin-right: 0.5rem;
          }
        }
      }
    }

    .attachment-actions {
      display: flex;
      gap: 0.375rem;
      flex-shrink: 0;

      button {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        padding: 0.25rem 0.5rem;
        border: 1px solid $border-color;
        border-radius: $radius-sm;
        background-color: $bg-white;
        color: $text-secondary;
        font-size: 0.625rem;
        font-weight: 500;
        cursor: pointer;
        transition: $transition;

        svg {
          width: 0.75rem;
          height: 0.75rem;
        }

        &:hover:not(:disabled) {
          background-color: $bg-light;
          border-color: $border-dark;
          color: $text-primary;
        }

        &:focus {
          outline: none;
          box-shadow: 0 0 0 2px rgba($primary-color, 0.2);
          border-color: $primary-color;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      }

      .download-button {
        color: $primary-color;
        border-color: $primary-color;

        &:hover:not(:disabled) {
          background-color: $primary-light;
        }

        .spinner-small {
          width: 0.75rem;
          height: 0.75rem;
          animation: rotate 1s linear infinite;

          .path {
            stroke: $primary-color;
            stroke-linecap: round;
            animation: dash 1.5s ease-in-out infinite;
            stroke-dasharray: 90, 150;
            stroke-dashoffset: 0;
          }
        }
      }

      .preview-button {
        color: $success-color;
        border-color: currentColor;

        &:hover {
          background-color: rgba($success-color, 0.1);
        }
      }
    }
  }
}

// Animations
@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
}

// Email content formatting styles - Enhanced for Gmail/Outlook quality
.email-content-formatted,
.email-html-content {
  line-height: 1.6;
  color: $text-primary;
  font-size: 0.875rem;
  max-width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;

  &.modern-email-display {
    // Gmail-style modern display enhancements
    font-family: "Google Sans", Roboto, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  .email-paragraph {
    margin: 0 0 1rem 0;
    color: $text-primary;
    line-height: 1.7;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .email-link {
    color: #1a73e8; // Gmail blue
    text-decoration: none;
    border-bottom: 1px solid transparent;
    transition: $transition;
    cursor: pointer;

    &:hover {
      color: #1557b0;
      text-decoration: underline;
    }

    &:active {
      color: #0d47a1;
    }

    &.email-address {
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;

      .email-icon {
        font-size: 0.8em;
        opacity: 0.8;
      }
    }

    &.phone-number {
      font-weight: 500;
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;

      .phone-icon {
        font-size: 0.8em;
        opacity: 0.8;
      }
    }
  }

  // Enhanced image handling
  .email-image,
  img {
    max-width: 100%;
    height: auto;
    border-radius: 4px;
    margin: 0.5rem 0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &[width] {
      width: auto !important;
      max-width: 100% !important;
    }
  }

  // Code formatting like GitHub/modern editors
  .email-code-block {
    background-color: #f6f8fa;
    border: 1px solid #e1e4e8;
    border-radius: 6px;
    padding: 1rem;
    margin: 1rem 0;
    overflow-x: auto;
    font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas,
      monospace;
    font-size: 0.8rem;
    line-height: 1.45;

    code {
      background: none;
      padding: 0;
      border: none;
      font-size: inherit;
    }
  }

  .email-inline-code {
    background-color: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 3px;
    padding: 0.125rem 0.25rem;
    font-family: "SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas,
      monospace;
    font-size: 0.8em;
    color: #d63384;
  }

  // Enhanced list styling
  .email-list {
    margin: 1rem 0;
    padding-left: 1.5rem;

    .email-list-item {
      margin: 0.5rem 0;
      line-height: 1.6;

      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  // Modern header styling
  .email-header {
    font-weight: 600;
    font-size: 1.1em;
    color: #1f2937;
    margin: 1.5rem 0 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #e5e7eb;
    text-transform: none;
    letter-spacing: 0;

    &:first-child {
      margin-top: 0;
    }
  }

  // Enhanced signature styling
  .email-signature {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
    color: #6b7280;
    font-size: 0.85em;
    line-height: 1.5;
  }

  // Quoted content styling (email replies)
  .email-quoted {
    margin: 1rem 0;
    padding: 1rem;
    border-left: 4px solid #e5e7eb;
    background-color: #f9fafb;
    color: #6b7280;
    font-size: 0.85em;
    position: relative;

    &::before {
      content: "Previous message";
      position: absolute;
      top: -0.5rem;
      left: 0.5rem;
      background: #f9fafb;
      padding: 0 0.5rem;
      font-size: 0.7em;
      color: #9ca3af;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  // Enhanced styling for various elements
  .email-div {
    margin: 0.5rem 0;

    &:empty {
      display: none;
    }
  }

  .email-center {
    text-align: center;
    margin: 1rem 0;
  }

  .email-styled-text {
    display: inline;
  }

  .email-signature {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid $border-color;
    color: $text-muted;
    font-style: italic;
    font-size: 0.9em;
  }

  .email-quote {
    margin: 1rem 0;
    padding: 0.75rem 1rem;
    border-left: 4px solid $primary-color;
    background-color: rgba($primary-color, 0.05);
    color: $text-secondary;
    font-style: italic;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  .email-header {
    margin: 1.5rem 0 1rem;
    font-size: 1.1em;
    font-weight: 600;
    color: $text-primary;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .email-table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
    background-color: $bg-white;
    border: 1px solid $border-color;
    border-radius: $radius;
    overflow: hidden;

    th,
    td {
      padding: 0.75rem;
      border-bottom: 1px solid $border-color;
      text-align: left;
    }

    th {
      background-color: $bg-light;
      font-weight: 600;
      color: $text-primary;
    }

    tr:last-child td {
      border-bottom: none;
    }

    tr:hover {
      background-color: rgba($primary-color, 0.02);
    }
  }

  .email-button,
  .email-button-link {
    display: inline-block;
    padding: 0.75rem 1.5rem;
    background: linear-gradient(135deg, #1a73e8 0%, #1557b0 100%);
    color: $text-white !important;
    text-decoration: none !important;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    cursor: pointer;
    transition: all 0.3s ease;
    margin: 0.5rem 0.25rem;
    box-shadow: 0 4px 12px rgba(26, 115, 232, 0.3);
    text-align: center;
    min-width: 120px;

    // Gmail-style button appearance
    background-clip: padding-box;
    border: 1px solid transparent;

    &:hover {
      background: linear-gradient(135deg, #1557b0 0%, #0d47a1 100%);
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(26, 115, 232, 0.4);
      color: $text-white !important;
      text-decoration: none !important;
    }

    &:active {
      transform: translateY(-1px);
      box-shadow: 0 3px 10px rgba(26, 115, 232, 0.3);
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 3px rgba(26, 115, 232, 0.3);
    }
  }

  // Handle preformatted text and code blocks
  pre,
  code {
    background-color: $bg-gray;
    border: 1px solid $border-color;
    border-radius: $radius-sm;
    font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace;
    font-size: 0.85em;
  }

  pre {
    padding: 1rem;
    overflow-x: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  code {
    padding: 0.2rem 0.4rem;
    color: #d63384;
  }

  // Handle lists
  ul,
  ol {
    margin: 1rem 0;
    padding-left: 1.5rem;

    li {
      margin: 0.25rem 0;
    }
  }

  // Handle images in emails
  img {
    max-width: 100%;
    height: auto;
    border-radius: $radius;
    box-shadow: $shadow-sm;
    margin: 0.5rem 0;
  }

  // Handle headings
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 1.5rem 0 0.75rem;
    color: $text-primary;
    line-height: 1.3;

    &:first-child {
      margin-top: 0;
    }
  }

  h1 {
    font-size: 1.5em;
    font-weight: 700;
  }
  h2 {
    font-size: 1.3em;
    font-weight: 600;
  }
  h3 {
    font-size: 1.1em;
    font-weight: 600;
  }
  h4 {
    font-size: 1em;
    font-weight: 600;
  }
  h5 {
    font-size: 0.9em;
    font-weight: 600;
  }
  h6 {
    font-size: 0.85em;
    font-weight: 600;
  }

  // Handle horizontal rules
  hr {
    margin: 2rem 0;
    border: none;
    height: 1px;
    background-color: $border-color;
  }

  // Handle blockquotes (general)
  blockquote {
    margin: 1rem 0;
    padding: 0.75rem 1rem;
    border-left: 4px solid $border-dark;
    background-color: $bg-light;
    color: $text-secondary;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Responsive Design
@media (max-width: 640px) {
  .message-header {
    padding: 1rem 1rem 0.5rem;

    .message-sender {
      gap: 0.5rem;

      .sender-avatar {
        width: 2rem;
        height: 2rem;
        font-size: 0.625rem;
      }
    }
  }

  .message-content {
    padding: 0 1rem 1rem;
  }

  .message-attachments {
    .attachment-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;

      .attachment-actions {
        width: 100%;
        justify-content: space-between;
      }
    }
  }
}
</style>
